import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRUPOS = [
  { titulo: "Documentos", items: [
    ["certificado_pdf", "Certificado Obra"], ["factura_pdf", "Factura"], ["anexo_pdf", "Anexo I"], ["fotos_pdf", "Fotos Actuación"],
    ["cert_energetico_inicial", "Certificado energético inicial"], ["registro_cee", "Justificante del registro"], ["xml_cee", "XML"],
    ["acuerdo_pdf", "Acuerdo Cesión"],
  ] },
  { titulo: "Otros documentos", items: [
    ["dni", "DNI"], ["tasa_registro_cee", "Tasa Registro CEE"], ["etiqueta_certificado", "Etiqueta Certificado"],
    ["justificante_cesion", "Justificante Cesión"], ["plantilla_entrega", "Plantilla Entrega CAE"], ["declaracion_tableta", "Declaración firmada en tableta"],
  ] },
  { titulo: "Datos", items: [
    ["datos_direccion", "Dirección y propietario"], ["datos_utm", "UTM (solo Navarra)"], ["datos_ficha", "Ficha: zona, año, CEF, superficies, inversión"],
  ] },
];
const TODOS = GRUPOS.flatMap((g) => g.items.map(([k]) => k));
const NOMBRES = Object.fromEntries(GRUPOS.flatMap((g) => g.items));

export default function GreenflexReemplazoRes022({ dossierId }) {
  const [abierto, setAbierto] = useState(false);
  const [marcados, setMarcados] = useState(new Set());
  const [enviando, setEnviando] = useState(false);
  const [resultados, setResultados] = useState(null);
  const [historial, setHistorial] = useState([]);

  const cargarHistorial = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/greenflex-reemplazo-res022/${dossierId}`);
      setHistorial(res.data.reemplazos);
    } catch { /* sin historial */ }
  }, [dossierId]);

  useEffect(() => { cargarHistorial(); }, [cargarHistorial]);

  const fecha = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const alternar = (k) => setMarcados((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const reemplazar = async () => {
    const lista = [...marcados].map((k) => NOMBRES[k]).join(", ");
    if (!window.confirm(`¿Reemplazar en Greenflex: ${lista}?`)) return;
    setEnviando(true);
    setResultados(null);
    try {
      const res = await axios.post(`${API}/greenflex-reemplazo-res022/${dossierId}`, { items: [...marcados] });
      setResultados(res.data.resultados);
      cargarHistorial();
      toast[res.data.resultados.every((r) => r.ok) ? "success" : "warning"]("Reemplazo terminado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al reemplazar en Greenflex");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="greenflex-reemplazo">
      <button className="flex items-center gap-2 w-full text-left text-sm font-medium text-slate-700" onClick={() => setAbierto(!abierto)}>
        <span className="flex-1">
          Reemplazar en Greenflex
          {historial[0] && <span className="block text-xs font-normal text-slate-500">Último reemplazo: {fecha(historial[0].at)}</span>}
        </span>
        {abierto ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {abierto && (
        <div className="mt-3 space-y-3">
          {GRUPOS.map((g) => (
            <div key={g.titulo} className="space-y-1">
              <p className="text-xs font-medium text-slate-500">{g.titulo}</p>
              {g.items.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={marcados.has(k)} onChange={() => alternar(k)} />
                  {label}
                </label>
              ))}
            </div>
          ))}
          <div className="flex items-center gap-3">
            <button className="text-xs underline text-slate-500" onClick={() => setMarcados(marcados.size === TODOS.length ? new Set() : new Set(TODOS))}>
              {marcados.size === TODOS.length ? "Quitar todo" : "Seleccionar todo"}
            </button>
            <button
              onClick={reemplazar}
              disabled={!marcados.size || enviando}
              className="ml-auto inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-40"
            >
              {enviando && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Reemplazar lo seleccionado
            </button>
          </div>
          {historial.length > 0 && (
            <div className="text-xs text-slate-500 space-y-0.5">
              <p className="font-medium">Reemplazos anteriores</p>
              {historial.map((h, i) => <p key={i}>{fecha(h.at)} · {h.items.map((k) => NOMBRES[k] || k).join(", ") || "sin cambios correctos"}</p>)}
            </div>
          )}
          {resultados && resultados.map((r) => (
            <p key={r.item} className={`text-xs flex items-start gap-1 ${r.ok ? "text-emerald-700" : "text-red-700"}`}>
              {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <XCircle className="w-3.5 h-3.5 shrink-0" />}
              <span>{NOMBRES[r.item]}{r.ok ? ": reemplazado y verificado" : `: ${r.detalle}`}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
